import Backbone from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileState, {FileStateAttributes} from 'filestate/FileState';
import * as navigation from 'Navigation';
import StorableResultsTabState from 'results/linked-results/StorableResultsTabState';
import {shouldDisablePrimaryLinkColumn} from 'runs/shouldDisablePrimaryLinkColumn';
import ContainerTestOrdersTabState from 'sample-tests/ui/test-orders/ContainerTestOrdersTabState';
import EntityStore from 'stores/EntityStore';
import {asJQueryPromise} from 'util/Promises';

import * as ContainerDAO from '../ContainerDAO';
import StorableStore from '../StorableStore';
import ContainerTabState from './ContainerTabState';

class ContainerToolbarState extends Backbone.Model {}

export default class ContainerFileState extends FileState {
  static fileType: string;
  static openFile: (fileId: string) => JQueryPromise<ContainerFileState>;

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new ContainerToolbarState(),
      statusbar: this.hasResults()
        ? new statusbar.ContainerStatusBarState()
        : new statusbar.SingleBufferStatusBarState(),
    };
  }

  editURL(): string {
    return ContainerDAO.containerEditorURL(this.id);
  }

  name(): string {
    return this.get('name') || 'Loading...';
  }

  archiveRecord(): ArchiveRecord {
    return this.get('archiveRecord');
  }

  setArchiveRecord(archiveRecord: ArchiveRecord): void {
    this.set({archiveRecord});
  }

  permissions(): Permissions {
    return this.get('permissions');
  }

  setPermissions(permissions: Permissions): void {
    this.set({permissions});
  }

  hasResults(): boolean {
    return (
      isFeatureEnabled({feature: ResultsFeature.RESULTS_SHOW_DETAILS, item: {id: this.id}}) &&
      shouldDisablePrimaryLinkColumn() &&
      EntityStore.currentUser()
    );
  }

  hasTestOrders(): boolean {
    return BenchlingConfig.SAMPLE_TESTS_ENABLE;
  }

  initialize(...args: Array<unknown>): void {
    // @ts-expect-error
    super.initialize(...args);
    this.changeListener = this.changeListener.bind(this);
    this.registerListeners();

    const buffers = new navigation.BufferStates([new navigation.BufferState(), new navigation.BufferState()]);
    this.set({buffers});
    buffers
      .at(0)
      .tabs()
      .add(new ContainerTabState({containerId: this.id}));

    if (this.hasResults()) {
      buffers
        .at(1)
        .tabs()
        .add(new StorableResultsTabState({storableId: this.id}));
    }
    if (this.hasTestOrders()) {
      buffers
        .at(1)
        .tabs()
        .add(new ContainerTestOrdersTabState({modelId: this.id}));
    }
    this.initIsWorkspaceSplitFromPreference();
  }

  registerTitleAndURLSync(): void {
    this.on('change:name', () => {
      this.syncTitleAndURL();
    });
  }

  changeListener(): void {
    const container = StorableStore.getContainer(this.id);
    if (container) {
      this.set({name: container.name, archiveRecord: container.archiveRecord});
    }
  }

  registerListeners(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    StorableStore.addChangeListener(this.changeListener);
  }

  cleanUp(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    StorableStore.removeChangeListener(this.changeListener);
  }
}

ContainerFileState.fileType = ModelTypeConstants.CONTAINER;

ContainerFileState.openFile = (fileId: string): JQueryPromise<ContainerFileState> =>
  asJQueryPromise(Promise.resolve(new ContainerFileState({id: fileId})));
